/* eslint-disable @typescript-eslint/ban-ts-comment */
import { FC } from "react";
import { useSelector } from "react-redux";

import ErrorComponent from "web/Layout/Common/ErrorComponent";
import Loading from "web/Layout/Common/Loading";

import { ICategory } from "web/types/Category";
import type { ICategoryCount } from "web/types/CategoryCount";

import useDataCachedCategories from "web/features/useDataCached/useDataCachedCategories";

import useCategoryCount from "./utils/useCategoryCount";
import useCategoryIdsAmount from "./utils/useCategoryIdsAmount";
import useTreeAndIds from "./utils/useTreeAndIds";
import WebViewList from "./webViewList";

interface INavigationListContainerWithCategoryDataProps {
  paths: string;
}

const NavigationListContainerWithCategoryData: FC<
  INavigationListContainerWithCategoryDataProps
> = ({ paths }) => {
  const {
    root_category_id: rootCategoryId,
    token,
    id: storeId,
  } = useSelector((state) => state.app.storeConfig);
  const { tree, ids } = useTreeAndIds({ paths, rootCategoryId });

  const {
    loading: dataCachedCategoriesLoading,
    error: dataCachedCategoriesError,
    data,
  } = useDataCachedCategories({
    // @ts-ignore
    ids,
    skip: !token,
  }) as { loading: boolean; error: unknown; data: ICategory[] };

  const {
    data: categoriesCountData,
    loading: categoriesCountLoading,
    error: categoriesCountError,
  } = useCategoryCount(token, storeId, ids);

  const loading = dataCachedCategoriesLoading || categoriesCountLoading;
  const error = dataCachedCategoriesError || categoriesCountError;

  const categoryIdsAmounts: ICategoryCount[] =
    useCategoryIdsAmount(categoriesCountData);

  switch (true) {
    case !token:
      return null;
    case !!loading: {
      return <Loading />;
    }
    case !!error: {
      return <ErrorComponent />;
    }
    case !!data.length: {
      return (
        <WebViewList
          rootCategoryId={rootCategoryId}
          categoriesTree={tree}
          categoriesData={data}
          categoriesSizes={categoryIdsAmounts}
        />
      );
    }
    default: {
      return null;
    }
  }
};

export default NavigationListContainerWithCategoryData;
